import { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Card, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Tooltip, Skeleton, InputAdornment, TextField,
  Dialog, DialogTitle, DialogContent, IconButton, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon, History as HistoryIcon, Visibility as ViewIcon, Close as CloseIcon } from '@mui/icons-material';

// Components
import { PageHeader } from '@/common-components/layout';

// Hooks
import { useAudit, useUrlPagination, useUrlState } from '@/common-hooks';
import { useAuthentication } from '@/common-hooks/use-authentication';

// Types
import type { AuditLog } from '@/common-types';

// Constants
import { PERMISSIONS } from '@/common-constants/permissions';
import { KNOWN_AUDIT_ACTIONS } from '@/common-constants/audit-actions';

const AUDIT_ACTION_FILTER_ALL = 'all';
/** Avoid query key `action` — conflicts with OAuth/redirect patterns; batch updates with pagination. */
const URL_AUDIT_ACTION = 'audit_action';
const URL_AUDIT_PAGE_KEY = 'audit';
const URL_Q_KEY = 'q';

// --- Helpers for human-readable audit display ---
function formatActionLabel(action: string): string {
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function LogDetailDialog({ log, onClose }: { log: AuditLog | null; onClose: () => void }) {
  if (!log) return null;
  const prettyJson = JSON.stringify(log, null, 2);

  return (
    <Dialog open={!!log} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
        <Chip label={formatActionLabel(log.action)} size="small" color="primary" />
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1,
            fontSize: '0.8125rem',
            fontFamily: 'monospace',
            overflow: 'auto',
            maxHeight: '70vh',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {prettyJson}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default function Audit() {
  const { hasPermission } = useAuthentication();
  const canAccessAudit = hasPermission(PERMISSIONS.AUDIT_LOGS);
  const { logs, loading, error, refresh, hasMore, lastEvaluatedKey } = useAudit();
  
  const { page, rowsPerPage, setPage, setRowsPerPage } = useUrlPagination(URL_AUDIT_PAGE_KEY);
  const { value: searchQuery } = useUrlState(URL_Q_KEY, '');
  const { value: selectedActionFilter } = useUrlState(URL_AUDIT_ACTION, AUDIT_ACTION_FILTER_ALL);
  const [, setSearchParams] = useSearchParams();
  
  const [localError, setLocalError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const fetchingRef = useRef(false);
  useEffect(() => {
    if (canAccessAudit && !fetchingRef.current) {
      fetchingRef.current = true;
      refresh({ limit: 100 }).finally(() => { fetchingRef.current = false; });
    }
  }, [canAccessAudit, refresh]);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    try {
      setLoadingMore(true);
      await refresh({ limit: 100, startKey: lastEvaluatedKey });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to load more logs');
    } finally {
      setLoadingMore(false);
    }
  };

  const actionDropdownOptions = useMemo(() => {
    const merged = new Set<string>(KNOWN_AUDIT_ACTIONS);
    for (const log of logs) {
      if (log.action) merged.add(log.action);
    }
    if (
      selectedActionFilter !== AUDIT_ACTION_FILTER_ALL &&
      !merged.has(selectedActionFilter)
    ) {
      merged.add(selectedActionFilter);
    }
    return [...merged].sort((a, b) => a.localeCompare(b));
  }, [logs, selectedActionFilter]);

  const filteredLogs = useMemo(() => {
    let result = logs;

    if (selectedActionFilter !== AUDIT_ACTION_FILTER_ALL) {
      result = result.filter((log) => log.action === selectedActionFilter);
    }

    if (!searchQuery.trim()) return result;
    const q = searchQuery.toLowerCase().trim();
    return result.filter((log) => {
      const d = log.details || {};
      const actionLabel = formatActionLabel(log.action).toLowerCase();
      return (
        log.action.toLowerCase().includes(q) ||
        actionLabel.includes(q) ||
        d.actionByUser?.name?.toLowerCase().includes(q) ||
        d.actionByUser?.email?.toLowerCase().includes(q) ||
        d.resource?.label?.toLowerCase().includes(q) ||
        d.resource?.id?.toLowerCase().includes(q) ||
        d.resource?.type?.toLowerCase().includes(q) ||
        JSON.stringify(log.details || {}).toLowerCase().includes(q)
      );
    });
  }, [logs, searchQuery, selectedActionFilter]);

  const paginatedLogs = useMemo(() => 
    filteredLogs.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredLogs, page, rowsPerPage]
  );

  const handleActionFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    // Single URL update: two separate setSearchParams calls each see the same `prev` and the second can drop `audit_action`.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(URL_AUDIT_ACTION, value);
        next.set(`${URL_AUDIT_PAGE_KEY}_p`, '0');
        return next;
      },
      { replace: true }
    );
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const getActionColor = (action: string) => {
    if (action.endsWith('_DELETED')) return 'error';
    if (action.endsWith('_UPDATED')) return 'info';
    if (action.endsWith('_CREATED')) return 'success';
    return 'default';
  };

  if (!canAccessAudit) {
    return <Alert severity="warning">You need audit log access permission to access this page.</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="Audit Logs"
        subtitle={`${filteredLogs.length} log entries`}
        actions={
          <>
            <FormControl size="small" sx={{ minWidth: 260, maxWidth: 360 }}>
              <InputLabel id="audit-action-filter-label">Action</InputLabel>
              <Select
                labelId="audit-action-filter-label"
                id="audit-action-filter"
                value={selectedActionFilter}
                label="Action"
                onChange={handleActionFilterChange}
              >
                <MenuItem value={AUDIT_ACTION_FILTER_ALL}>All actions</MenuItem>
                {actionDropdownOptions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {formatActionLabel(action)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set(URL_Q_KEY, value);
                    next.set(`${URL_AUDIT_PAGE_KEY}_p`, '0');
                    return next;
                  },
                  { replace: true }
                );
              }}
              sx={{ width: 200 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => refresh({ limit: 100 })}
              disabled={loading}
            >
              Refresh
            </Button>
          </>
        }
      />

      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {error || localError}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table 
            size="small"
            sx={{ 
              tableLayout: 'auto',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Timestamp</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Action</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Action By</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Resource</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={150} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={150} /></TableCell>
                    <TableCell><Skeleton width={200} /></TableCell>
                    <TableCell><Skeleton width={150} /></TableCell>
                  </TableRow>
                ))
              ) : paginatedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Box color="text.secondary">{searchQuery.trim() || selectedActionFilter !== AUDIT_ACTION_FILTER_ALL ? 'No matches for the selected filters' : 'No audit logs'}</Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.875rem' }}>
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip 
                        label={formatActionLabel(log.action)} 
                        size="small" 
                        color={getActionColor(log.action)}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {log.details?.actionByUser ? (
                        <Tooltip title={log.details.actionByUser.email} arrow>
                          <Box>
                            <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{log.details.actionByUser.name || log.details.actionByUser.email}</Box>
                            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{log.details.actionByUser.email}</Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>—</Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {log.details?.resource ? (
                        <Tooltip title={`${log.details.resource.type}: ${log.details.resource.id}`} arrow>
                          <Box>
                            <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{log.details.resource.label || log.details.resource.id}</Box>
                            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{log.details.resource.type}</Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>—</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View log">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLog(log);
                          }}
                          aria-label="View log details"
                          sx={{ color: 'text.secondary' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredLogs.length > 5 && (
          <TablePagination
            component="div"
            count={filteredLogs.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
        {hasMore && (
          <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loadingMore || loading}
              startIcon={<RefreshIcon />}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
      </Card>

      <LogDetailDialog log={selectedLog} onClose={() => setSelectedLog(null)} />
    </Box>
  );
}

